import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { PageHero, useTitle } from "../components/PageHero";
import { Reveal, Tag } from "../components/Reveal";
import { SITE, waLink } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REASONS = ["Sales", "Technical Support", "RFQ", "Maintenance / Repair", "Partnerships"];

const inputCls = "w-full border border-seam bg-obsidian px-4 py-3.5 text-sm text-frost placeholder:text-cadmium/50 focus:border-spark focus:outline-none";

export default function Contact() {
  useTitle("Contact SABAKT — Riyadh, Saudi Arabia");
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", reason: "Sales", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/enquiry`, form);
      setSent(true);
      toast.success(res.data.message);
    } catch {
      toast.error("Submission failed. Please try WhatsApp or email instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main>
      <PageHero eyebrow="Contact · Riyadh, Kingdom of Saudi Arabia" testid="contact-hero"
        title="Talk to SABAKT." accent="SABAKT."
        text="Phone, email, WhatsApp or the form below — choose the channel that suits the urgency. RFQs with drawings get the fastest technical response." />

      <section className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-8 lg:grid-cols-[1fr_1.2fr] lg:px-16">
          <Reveal>
            <div className="space-y-px border border-seam bg-seam">
              {[
                { icon: MapPin, label: "Location", value: SITE.city },
                { icon: Phone, label: "Phone", value: SITE.phone, href: `tel:${SITE.phone.replace(/\s/g, "")}`, testid: "contact-phone" },
                { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}`, testid: "contact-email" },
                { icon: Clock, label: "Working Hours", value: SITE.hours },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4 bg-iron p-6">
                  <c.icon size={20} className="mt-0.5 shrink-0 text-spark" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cadmium/70">{c.label}</p>
                    {c.href ? (
                      <a href={c.href} data-testid={c.testid} className="mt-1 block text-base font-semibold text-frost hover:text-spark">{c.value}</a>
                    ) : (
                      <p className="mt-1 text-base font-semibold text-frost">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-btn"
              className="mt-6 flex items-center justify-center gap-3 bg-[#25D366] px-6 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-opacity hover:opacity-90">
              <MessageCircle size={20} /> WhatsApp SABAKT
            </a>
            <div className="mt-8 overflow-hidden border border-seam" data-testid="contact-map">
              <iframe
                title="SABAKT — Riyadh, Saudi Arabia"
                src="https://www.google.com/maps?q=Riyadh,+Saudi+Arabia&output=embed"
                className="h-64 w-full grayscale-[0.3]"
                loading="lazy"
              />
              <p className="bg-iron px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-cadmium/60">
                Map shows Riyadh — exact location pin [VERIFY WITH SABAKT]
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="border border-seam bg-iron p-8 lg:p-10" data-testid="contact-form-card">
              <Tag>Enquiry Form</Tag>
              <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-frost">Send an Enquiry</h2>
              {sent ? (
                <div className="mt-8 border border-spark/50 bg-obsidian p-8 text-center" data-testid="contact-success">
                  <p className="font-display text-2xl font-bold uppercase text-spark">Enquiry Received</p>
                  <p className="mt-3 text-sm text-cadmium">Thank you. The SABAKT team will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-8 grid gap-5 sm:grid-cols-2">
                  <input required placeholder="Name *" value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                  <input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                  <input placeholder="Saudi Mobile (+966 ...)" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                  <input placeholder="Company" value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company-input" />
                  <select value={form.reason} onChange={set("reason")} className={inputCls} data-testid="contact-reason-select">
                    {REASONS.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <textarea required placeholder="Your message *" rows={4} value={form.message} onChange={set("message")}
                    className={`${inputCls} sm:col-span-2`} data-testid="contact-message-input" />
                  <button type="submit" disabled={sending} data-testid="contact-submit-btn"
                    className="bg-gold px-7 py-4 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:bg-spark disabled:opacity-50 sm:col-span-2">
                    {sending ? "Sending..." : "Send Enquiry"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
